<template>
  <div class="search" :style="{ height: `${ windowHeight }px` }">
    <div class="search__overlay" @click="$emit('close')" />

    <!-- Skeleton Loading -->
    <div class="search__body" v-if="q.length > 1">
      <div class="page-container" v-if="loading">
        <h3 class="skeleton" style="height: 20px; width: 160px;" />
      </div>

      <div class="specialists page-container" style="margin-top: 20px" v-if="loading">
        <div class="specialists__card">
          <div class="skeleton" style="border-radius: 9999px; border: 0 !important; overflow: hidden;" />
          <h4 class="skeleton" style="height: 12px; margin-top: 10px" />
        </div>
        <div class="specialists__card">
          <div class="skeleton" style="border-radius: 9999px; border: 0 !important; overflow: hidden;" />
          <h4 class="skeleton" style="height: 12px; margin-top: 10px" />
        </div>
        <div class="specialists__card">
          <div class="skeleton" style="border-radius: 9999px; border: 0 !important; overflow: hidden;" />
          <h4 class="skeleton" style="height: 12px; margin-top: 10px" />
        </div>
        <div class="specialists__card">
          <div class="skeleton" style="border-radius: 9999px; border: 0 !important; overflow: hidden;" />
          <h4 class="skeleton" style="height: 12px; margin-top: 10px" />
        </div>
      </div>

      <div class="page-container" v-if="loading">
        <h3 class="skeleton" style="height: 20px; width: 160px;" />

        <div class="articles">
          <div class="articles__card">
            <div class="skeleton" />
            <div class="articles__card__meta">
              <h4 class="skeleton" style="height: 15px; max-width: 380px; margin-top: 10px" />
              <h4 class="skeleton" style="height: 15px; max-width: 280px; margin-top: 5px" />
            </div>
          </div>
          <div class="articles__card">
            <div class="skeleton" />
            <div class="articles__card__meta">
              <h4 class="skeleton" style="height: 15px; max-width: 380px; margin-top: 10px" />
              <h4 class="skeleton" style="height: 15px; max-width: 280px; margin-top: 5px" />
            </div>
          </div>
          <div class="articles__card">
            <div class="skeleton" />
            <div class="articles__card__meta">
              <h4 class="skeleton" style="height: 15px; max-width: 380px; margin-top: 10px" />
              <h4 class="skeleton" style="height: 15px; max-width: 280px; margin-top: 5px" />
            </div>
          </div>
        </div>
      </div>

      <!-- No results returned -->
      <div class="page-container" v-else-if="articlesCount === 0 && specialistsCount === 0">
        Nu s-au găsit rezultate pentru căutarea <b>"{{ q }}"</b>
      </div>

      <!-- Display previews -->
      <template v-else>
        <template v-if="specialistsCount !== 0">
          <h3 class="page-container">
            Specialiști ({{ specialistsCount }})
          </h3>

          <div class="specialists page-container" :style="articlesCount === 0 ? { marginBottom: '-15px' } : {}">
            <router-link
              @click.native="close"
              class="specialists__card"
              v-for="specialist of specialists"
              :to="`/specialists/profile/${ specialist.id }`"
              :key="'specialist-' + specialist.id"
            >
              <img :src="specialist.image_url" />
              <h4>{{ specialist.full_name }}</h4>
            </router-link>
            <router-link
              @click.native="close"
              class="specialists__card"
              to="/specialists/"
              v-if="specialistsCount > 3"
            >
              <div v-if="specialistsCount === 4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </div>
              <div v-else>
                +{{ specialistsCount - 3 }}
              </div>
              <h4>Mai multe</h4>
            </router-link>
          </div>
        </template>
        <div class="page-container" v-if="articlesCount !== 0">
          <h3>Articole ({{ articlesCount }})</h3>

          <div class="articles">
            <router-link
              @click.native="close"
              class="articles__card"
              v-for="article of articles.slice(0, 3)"
              :to="`/articles/${ article.slug }`"
              :key="'article-' + article.id"
            >
              <img :src="article.image_url" />
              <div class="articles__card__meta">
                <h4>{{ article.title }}</h4>
                <p>
                  <span v-for="(category,i) in article.categories"><span v-if="i != 0">, </span>{{category.name}}</span>
                </p>
              </div>
            </router-link>
          </div>

          <router-link
            @click.native="close"
            class="articles__more"
            v-if="articlesCount > 3"
            :to="{ path: '/articles', query: { q } }"
          >
            Vezi mai multe articole...
          </router-link>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js';

export default {
  props: [ 'q' ],
  data() {
    return {
      active           : false,
      activeSearch     : false,
      articles         : [],
      articlesCount    : 0,
      debounceInterval : null,
      id               : null,
      interval         : null,
      loading          : false,
      specialists      : [],
      specialistsCount : 0,
      windowHeight     : 0
    };
  },
  created() {
    document.body.style.overflow = 'hidden';
    window.addEventListener("resize", this.resize);
  },
  destroyed() {
    document.body.style.overflow = null;
    window.removeEventListener("resize", this.resize);

    if (this.interval) {
      clearInterval(this.interval);
    }
  },
  mounted() {
    if (this.q) {
      this.fetch();
    }

    this.resize();
    this.interval = setInterval(this.resize, 5000);
  },

  methods: {
    close() {
      this.$emit('close');
    },

    // underscore debounce function
    debounce(func, wait, immediate) {
      var timeout;
      return function() {
        var context = this, args = arguments;
        var later = function() {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    },

    debouceFetch() {
      if (this.loading === false) {
        this.loading = true;
      }

      if (this.debounceInterval === null) {
        this.debounceInterval = this.debounce(this.fetch, 500);
      } else {
        this.debounceInterval();
      }
    },

    async fetch() {
      if (this.q.length < 2) {
        return;
      }

      this.id      = Date.now().toString();
      this.loading = true;

      // Fetch
      const request = await axios.post('/global/search', {
        id : this.id,
        q  : this.q
      });

      // Reject un-wanted ids
      if (this.id !== JSON.parse(request.config.data).id) {
        return;
      }

      this.articles         = request.data.articles;
      this.articlesCount    = request.data.articles_count;
      this.specialists      = request.data.specialists;
      this.specialistsCount = request.data.specialists_count;

      this.loading = false;
    },

    resize() {
      this.windowHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      ) - 80;
    }
  },
  watch: {
    q(value) {
      this.debouceFetch();
    }
  }
}
</script>

<style lang="scss" scoped>
h3 {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 15px;
}

.page-container {
  max-width: 100%;
}

.search {
  left: 0;
  max-width: 100%;
  position: absolute;
  top: 79px;
  width: 100%;

  &__body {
    background: #ffffff;
    border-top: 1px solid #eff3f8;
    box-shadow: 0px 10px 30px -10px rgba(#000000, 0.1);
    padding-bottom: 20px;
    padding-top: 20px;
    position: relative;
    z-index: 50;
    width: 100%;
  }

  &__overlay {
    background: rgba(#000000, 0.4);
    height: 100%;
    left: 0;
    position: absolute;
    width: 100%;
    z-index: 40;
  }
}

.specialists {
  display: flex;
  margin-bottom: 15px;
  overflow: auto;
  width: 100%;

  &__card {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
    margin-right: 15px;
    max-width: 80px;
    min-width: 80px;
    text-decoration: none;
    width: 100%;

    &:last-child {
      margin-right: 0;
    }

    & > div,
    & > img {
      border: 2px solid $color-light-gray;
      border-radius: 9999px;
      height: 60px;
      margin-left: auto;
      margin-right: auto;
      max-width: 60px;
      object-fit: cover;
      transition: 100ms border-color;
      width: 60px;
    }

    & > div {
      align-items: center;
      background: $color-secondary;
      border-color: $color-secondary;
      color: #ffffff;
      display: flex;
      font-size: 21px;
      font-weight: 600;
      justify-content: center;
      line-height: 1;
    }

    &:hover {
      & > img {
        border-color: $color-primary;
      }
    }

    & > h4 {
      color: $color-gray;
      font-size: 13px;
      font-weight: 400;
      line-height: 1;
      margin-top: 5px;
      overflow: hidden;
      text-align: center;
      text-overflow: ellipsis;
      transition: 100ms color;
      white-space: nowrap;
    }

    &:hover {
      & > h4 {
        color: $color-primary;
      }
    }
  }
}

.articles {
  &__card {
    align-items: center;
    cursor: pointer;
    display: flex;
    margin-bottom: 15px;
    text-decoration: none;

    & > div:not(&__meta),
    & > img {
      height: 60px;
      object-fit: cover;
      max-width: 110px;
      min-width: 110px;
    }

    & > &__meta {
      flex: 1;
      margin-left: 15px;
      max-width: calc(100% - 125px);

      & > h4 {
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        color: $color-gray;
        display: -webkit-box;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.2;
        overflow: hidden;
        transition: 100ms color;
      }

      & > p {
        color: rgba(55, 52, 53, 0.5);
        font-size: 13px;
        font-weight: 400;
        line-height: 1;
        margin-top: 5px;
        max-width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }

    &:hover > &__meta > h4 {
      color: $color-primary;
    }
  }

  &__more {
    color: $color-primary;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
  }
}
</style>
